import { AlertCircle, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { InfoTip } from '@/components/ui/info-tip';
import { Typography } from '@/components/ui/typography';

import type { SavedCard } from '../../../../types/order.types';
import {
  BulkOrderHeader,
  CardStatusBadge,
  CardTypeIcon,
  EnterpriseBanner,
  capitalize,
} from '../utils/cardHelpers';

interface SavedCardsListProps {
  cards: SavedCard[];
  selectedCard: SavedCard | null;
  isCardSelected: (card: SavedCard) => boolean;
  onCardClick: (card: SavedCard) => void;
  onUpdate: (card: SavedCard) => void;
  onDelete: (card: SavedCard) => void;
  onSetDefault: (card: SavedCard) => void;
  onAddCard: () => void;
  isSkipCheckOut: boolean | null;
  isMaxCardCount: boolean;
  totalAmount: number;
  isBulkOrder: boolean;
  fileNameInfo?: string | null;
  orderCount?: string | number;
  failedCountInfo?: string | null;
}

const cardKey = (card: SavedCard) =>
  `${card.customerPaymentProfileId || card.cardNumber}-${card.expirationMonth}-${card.expirationYear}`;

export default function SavedCardsList({
  cards,
  isCardSelected,
  onCardClick,
  onUpdate,
  onDelete,
  onSetDefault,
  onAddCard,
  isSkipCheckOut,
  isMaxCardCount,
  totalAmount,
  isBulkOrder,
  fileNameInfo,
  orderCount,
  failedCountInfo,
}: SavedCardsListProps) {
  const disabled = !!isSkipCheckOut || totalAmount === 0;
  const hasCards = cards.length > 0;
  // Every entity manages its own cards; monthly invoicing is the only gate.
  const showEmptyAddCta = !hasCards && !isSkipCheckOut;

  return (
    <div>
      {isSkipCheckOut && <EnterpriseBanner />}
      {isBulkOrder && (
        <BulkOrderHeader
          fileNameInfo={fileNameInfo}
          orderCount={orderCount}
          failedCountInfo={failedCountInfo}
          hasCards={hasCards}
        />
      )}

      {hasCards && (
        <>
          <Typography as="h2" variant="heading-md" className="mb-3">
            Saved Cards
          </Typography>

          {isMaxCardCount && !isSkipCheckOut && (
            <div className="mb-3 flex items-center gap-2 text-muted-foreground">
              <InfoTip
                label="Why Is the Limit Reached?"
                icon={AlertCircle}
                iconClassName="h-5 w-5"
                side="right"
                triggerClassName="rounded-full text-destructive"
              >
                You have reached the maximum limit to save credit cards.
                Please remove an existing card before adding a new one.
              </InfoTip>
              <Typography variant="body">
                Maximum limit reached to add cards
              </Typography>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {cards.map((card) => {
              const isSelected = isCardSelected(card) && !isSkipCheckOut;
              const statusBadge =
                card.cardExpiryStatus === 'Expired'
                  ? 'expired'
                  : card.cardExpiryStatus === 'Expiring soon'
                  ? 'expiringSoon'
                  : card.default === true
                  ? 'default'
                  : null;

              return (
                <div
                  key={cardKey(card)}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  aria-pressed={isSelected}
                  aria-disabled={disabled || undefined}
                  className={`rounded-lg border p-4 transition-colors ${
                    isSelected
                      ? 'border-primary ring-1 ring-primary'
                      : 'border-border'
                  } ${
                    disabled
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer hover:border-primary/60'
                  }`}
                  onClick={() => onCardClick(card)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onCardClick(card);
                    }
                  }}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <CardTypeIcon type={card.cardType} />
                    <Typography
                      variant="small"
                      className="text-muted-foreground"
                    >
                      CREDIT
                    </Typography>
                  </div>

                  <div className="flex flex-wrap gap-6">
                    <div className="flex flex-col gap-1">
                      <Typography variant="body-medium">
                        {card.firstName} {card.lastName}
                      </Typography>
                      <Typography
                        variant="body"
                        className="text-muted-foreground"
                      >
                        Zipcode: {card.zipCode}
                      </Typography>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Typography variant="body-medium">
                        {capitalize(card.cardType)} ••••{card.cardNumber}
                      </Typography>
                      <div className="flex flex-wrap items-center gap-2">
                        <Typography
                          variant="body"
                          className="text-muted-foreground"
                        >
                          Expires {card.expirationMonth}/{card.expirationYear}
                        </Typography>
                        {statusBadge && <CardStatusBadge status={statusBadge} />}
                      </div>
                    </div>
                  </div>

                  {!isSkipCheckOut && (
                    <div className="mt-3 flex items-center gap-2">
                      <Button
                        type="button"
                        variant="link"
                        // Standalone link-styled actions in their own row, so
                        // each carries the 44px touch floor itself — see
                        // button.tsx's link-variant comment.
                        className="h-auto px-0 max-lg:min-h-11"
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpdate(card);
                        }}
                      >
                        Update
                      </Button>
                      {card.default === false && (
                        <Button
                          type="button"
                          variant="link"
                          className="ml-auto h-auto px-0 max-lg:min-h-11"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSetDefault(card);
                          }}
                        >
                          Set as default
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="link"
                        className={`h-auto px-0 max-lg:min-h-11 ${
                          card.default === false ? '' : 'ml-auto'
                        }`}
                        data-testid="remove_id"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(card);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}

            {!isSkipCheckOut && (
              <Button
                type="button"
                variant="link"
                // Standalone link-styled action, so it carries the 44px touch
                // floor itself — see button.tsx's link-variant comment.
                className="self-start px-0 max-lg:min-h-11"
                onClick={onAddCard}
              >
                <Plus className="h-4 w-4" />
                Add payment method
              </Button>
            )}
          </div>
        </>
      )}

      {showEmptyAddCta && (
        <div className="flex justify-center py-8">
          <Button type="button" variant="outline" onClick={onAddCard}>
            <Plus className="h-4 w-4" />
            Add payment method
          </Button>
        </div>
      )}
    </div>
  );
}
